import { Button, Loader } from '@mantine/core'
import { IconAlertTriangle, IconCircleCheck } from '@tabler/icons-react'
import { Link } from 'react-router-dom'
import { usePatientMerge, useResumeMerge } from '@/hooks/usePatientMerge'

const CARD_CLASS = 'flex flex-col items-start gap-3 rounded-[14px] border bg-white px-6 py-5'

/** A link to the retained chart, by name. */
export function KeptChartLink({ patientId, name }: { patientId: string; name: string }) {
  return (
    <Link
      to={`/patients/${patientId}`}
      className="font-semibold !text-[#E5533D] no-underline hover:underline"
    >
      Open {name}&apos;s chart
    </Link>
  )
}

/**
 * A started merge, followed until it finishes: merging → "Records merged", or an interrupted run
 * with Resume. `onBackToReview` lets a restored merge return to its review, which Restore reopened.
 */
export function MergeProgress({
  mergeId,
  onBackToReview,
}: {
  mergeId: string
  onBackToReview?: () => void
}) {
  const { data: merge, isError, refetch } = usePatientMerge(mergeId)
  const resume = useResumeMerge()

  if (isError) {
    return (
      <div className={`${CARD_CLASS} border-[#E7E9EC]`}>
        <p className="text-sm text-[#6B7280]">Couldn&apos;t load the merge.</p>
        <Button variant="default" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    )
  }
  if (!merge) {
    return (
      <div className="flex justify-center py-10">
        <Loader />
      </div>
    )
  }

  const { keptPatient, mergedPatient } = merge

  // A super admin undid this merge after it finished (or is undoing it now).
  if (merge.state === 'restoring' || merge.state === 'restored') {
    return (
      <div className={`${CARD_CLASS} border-[#E7E9EC]`}>
        <h2 className="text-lg font-bold text-[#1F2733]">
          {merge.state === 'restoring' ? 'Restoring records…' : 'Merge restored'}
        </h2>
        {merge.state === 'restored' && (
          <p className="text-[13px] text-[#4A5160]">
            {mergedPatient.name} and {keptPatient.name} are separate records again.
          </p>
        )}
        {merge.state === 'restored' && onBackToReview && (
          <Button variant="default" onClick={onBackToReview}>
            Back to the review
          </Button>
        )}
      </div>
    )
  }

  // The server decides (R31): a failed run, or one still `in-progress` whose run died (not written
  // for 10 minutes) — the second would otherwise spin as "Merging…" forever. Still polled, slowly
  // (R36): a run judged dead that finishes after all replaces this with its result.
  if (merge.resumable) {
    return (
      <div className={`${CARD_CLASS} border-[#F2D3A1]`}>
        <h2 className="flex items-center gap-2 text-lg font-bold text-[#1F2733]">
          <IconAlertTriangle size={20} className="text-[#B26A00]" aria-hidden />
          The merge was interrupted.
        </h2>
        <p className="text-[13px] text-[#4A5160]">
          {mergedPatient.name} may not be fully retired yet. Resume finishes the remaining steps.
        </p>
        <Button
          loading={resume.isPending}
          onClick={() => resume.mutate({ mergeId: merge.mergeId })}
        >
          Resume merge
        </Button>
      </div>
    )
  }

  if (merge.runStatus === 'in-progress') {
    return (
      <div className={`${CARD_CLASS} border-[#E7E9EC]`}>
        <h2 className="flex items-center gap-2 text-lg font-bold text-[#1F2733]">
          <Loader size="sm" />
          Merging records…
        </h2>
        <p className="text-[13px] text-[#4A5160]">
          Retiring {mergedPatient.name}. You can leave this page; the merge continues.
        </p>
      </div>
    )
  }

  return (
    <div className={`${CARD_CLASS} border-[#9BD3B0]`}>
      <h2 className="flex items-center gap-2 text-lg font-bold text-[#1F2733]">
        <IconCircleCheck size={20} className="text-[#1E7B46]" aria-hidden />
        Records merged
      </h2>
      <p className="text-[13px] text-[#4A5160]">
        {keptPatient.name} is the active chart. {mergedPatient.name} is retired with its records
        {merge.deletesOn
          ? ` until they are deleted on ${new Date(merge.deletesOn).toLocaleDateString()}.`
          : '.'}
      </p>
      <KeptChartLink patientId={keptPatient.patientId} name={keptPatient.name} />
    </div>
  )
}
